<template>
  <div
    class="slider"
    ref="slider"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div
      class="slider-track"
      :style="{ transform: `translateX(${translateX}px)` }"
    >
      <slot></slot>
    </div>
    <div class="slider-dots" v-if="slideCount > 1">
      <button
        v-for="(_, index) in slideCount"
        :key="index"
        type="button"
        class="dot"
        :class="[
          { active: index === activeIndex },
          { '!bg-black': props.light },
        ]"
        :aria-label="`Folie ${index + 1} anzeigen`"
        :aria-current="index === activeIndex"
        @click="goTo(index)"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, useSlots, VNode, computed } from 'vue';

const props = defineProps<{
  light?: boolean;
}>();

const slider = ref<HTMLElement | null>(null);
const activeIndex = ref(0);
const translateX = ref(0);
const touchStartX = ref(0);
const touchMoveX = ref(0);

const slots = useSlots();

const getSlideCount = (nodes: VNode[] | undefined): number => {
  if (!nodes) {
    return 0;
  }
  let count = 0;
  nodes.forEach((node) => {
    if (typeof node.type === 'symbol') {
      // This is a fragment
      count += getSlideCount(node.children as VNode[]);
    } else {
      count++;
    }
  });
  return count;
};

const slideCount = computed(() => {
  return getSlideCount(slots.default?.());
});

onMounted(() => {
  window.addEventListener('resize', handleResize);
  handleResize();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const handleResize = () => {
  goTo(0); // Reset to first slide on resize
};

const goTo = (index: number) => {
  if (slider.value) {
    const slideWidth = slider.value.clientWidth;
    translateX.value = -index * slideWidth;
    activeIndex.value = index;
  }
};

const onTouchStart = (event: TouchEvent) => {
  touchStartX.value = event.touches[0].clientX;
  touchMoveX.value = touchStartX.value;
};

const onTouchMove = (event: TouchEvent) => {
  touchMoveX.value = event.touches[0].clientX;
};

const onTouchEnd = () => {
  const diff = touchMoveX.value - touchStartX.value;
  if (diff > 50 && activeIndex.value > 0) {
    goTo(activeIndex.value - 1);
  } else if (diff < -50 && activeIndex.value < slideCount.value - 1) {
    goTo(activeIndex.value + 1);
  }
};
</script>

<style>
.slider {
  overflow: hidden;
  position: relative;
  @apply flex flex-col;
}

.slider-track {
  display: flex;
  transition: transform 0.3s ease-in-out;
}

.slider-track > * {
  flex-shrink: 0;
  width: 100%;
}

.slider-dots {
  @apply flex justify-center gap-2 pt-6 md:pt-8;
  margin: 10px 0;
}

.dot {
  /* 24px tap target around an 8px visual dot: the background is clipped to
     the content box, so the padding stays invisible. */
  width: 24px;
  height: 24px;
  padding: 8px;
  border: 0;
  border-radius: 50%;
  background-color: #ffffff;
  background-clip: content-box;
  opacity: 0.2;
  cursor: pointer;
}

.dot.active {
  @apply opacity-100;
}
</style>
